/**
 * Loss History Detail (#33939) — the ONE date-normalization rule.
 *
 * Three writers used two date formats: A+/Verisk supplies `lossDate` as `yyyyMMdd`, while IMT and
 * manual entries use ISO `yyyy-MM-dd`. `new Date('20240601')` is an Invalid Date, so an un-normalized
 * A+ date read 0 losses in the FE Loss Summary and never matched an ISO entry in the same-day dedup.
 * Every writer runs its `lossDate` through this on build, and `hydrateLossEntryForForm` runs it on
 * already-persisted entries, so the field is ISO `yyyy-MM-dd` everywhere downstream.
 */

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
const YYYYMMDD = /^\d{8}$/;

const pad2 = (n: number): string => String(n).padStart(2, '0');

/**
 * Normalize a loss date to ISO `yyyy-MM-dd`, or `null` if absent/unparseable.
 * Pure string→string for the two formats the writers produce (no timezone round-trip); a lenient
 * UTC parse handles anything else without shifting the calendar day.
 */
export function normalizeLossDate(value: string | null | undefined): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  const trimmed = String(value).trim();
  if (!trimmed) {
    return null;
  }
  // Already ISO (accept an ISO datetime by taking its date part).
  if (ISO_DATE.test(trimmed)) {
    return trimmed;
  }
  if (trimmed.length > 10 && ISO_DATE.test(trimmed.slice(0, 10)) && trimmed[10] === 'T') {
    return trimmed.slice(0, 10);
  }
  // Verisk/A+ `yyyyMMdd`.
  if (YYYYMMDD.test(trimmed)) {
    return `${trimmed.slice(0, 4)}-${trimmed.slice(4, 6)}-${trimmed.slice(6, 8)}`;
  }
  // Anything else: parse as UTC so a `MM/DD/YYYY`-style value doesn't shift a day, then reformat.
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return `${parsed.getUTCFullYear()}-${pad2(parsed.getUTCMonth() + 1)}-${pad2(parsed.getUTCDate())}`;
}
